import React, { useEffect, useRef } from "react";
import Image from "next/image";
import useProductEvents from "@/hooks/eventing/useProductEvents";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  FormatSubType,
  FormatType,
} from "@/constants/tagular/main";
import { hyphenateForTagular } from "@/utils/tagular/helpers";
import { useInView } from "react-intersection-observer";

export type ProductSearchListItemProps = {
  id: string;
  productName: string;
  productCategory: string;
  partner: {
    key: string;
    fullName: string;
  };
  partnerLogoUrl: string;
  viewedSince?: number;
  timeToComplete?: number;
  subtype?: string;
  href: string;
  variant?: string;
  index?: number;
  location?: string;
};

const ProductSearchListItem = ({
  id,
  productName,
  productCategory,
  partner,
  partnerLogoUrl,
  viewedSince,
  timeToComplete,
  href,
  subtype = FormatSubType.SearchResult,
  index = 0,
  variant,
  location = "embedded-search",
}: ProductSearchListItemProps) => {
  const hasViewedRef = useRef(false);
  const { ref: inViewRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  const tagularProduct = {
    brand: partner.fullName,
    category: productCategory,
    location: location,
    formatType: FormatType.Search,
    formatSubtype: subtype,
    name: hyphenateForTagular(productName),
    position: index + 1, // 1-based position
    variant: variant,
    productId: id,
  };

  const { productClicked, productViewed } = useProductEvents({
    product: tagularProduct,
  });

  useEffect(() => {
    if (inView && !hasViewedRef.current) {
      hasViewedRef.current = true;
      productViewed(); // No arguments needed
    }
  }, [inView, productViewed]);

  const handleClick = () => {
    productClicked();
  };

  return (
    <a className="flex gap-4" ref={inViewRef} href={href} onClick={handleClick}>
      <Image
        width={92}
        height={43}
        aria-hidden="true"
        className="flex-shrink-0 bg-white border border-putty-400 p-2 h-10 w-20 rounded object-contain"
        src={partnerLogoUrl}
        alt={`${partner.fullName} logo`}
      />
      <div>
        <span className="font-bold line-clamp-1">{productName}</span>
        <div className="flex flex-wrap gap-1">
          <span className="text-xs text-black py-0.5">
            {partner.fullName} {timeToComplete && ` • ${timeToComplete} weeks`}
            {viewedSince && (
              <>
                <span className="text-black text-xs"> • </span>
                <span className="text-putty-600 text-xs">
                  {viewedSince} mins ago
                </span>
              </>
            )}
          </span>
        </div>
      </div>
    </a>
  );
};

export default ProductSearchListItem;
